/* eslint-disable import/no-named-as-default-member */
import ReactGA from "react-ga4";
import { DisplayPrice } from "../api/generated";
import Constants from "../constants";
import { Item } from "../models/item";
import { ItemStates } from "../models/itemStates";
import { SummaryItem } from "../models/summaryItem";

interface GaProduct {
    item_id?: string;
    item_name?: string;
    affiliation?: string;
    coupon?: string;
    discount?: number;
    index?: number;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string;
    location_id?: string;
    price?: number;
    quantity?: number;
}

function areCookiesEnabled() {
    try {
        document.cookie = "testcookie=test; Max-Age=10; SameSite=Strict;";
        const isEnabled = document.cookie.indexOf("testcookie") !== -1;
        document.cookie = "testcookie=; Max-Age=-1; SameSite=Strict;";
        return isEnabled;
    } catch {
        return false;
    }
}

function generateUniqueId() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
        /* eslint-disable no-bitwise */
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        /* eslint-enable no-bitwise */

        return v.toString(16);
    });
}

function getCurrentCleanedUrl(): URL {
    const url = new URL(window.location.href);
    url.searchParams.delete("config_string");
    url.searchParams.delete("encoded_config");
    url.hash = "";
    return url;
}

// The custom dimensions are not set globally in GA4, so any properties
// that are set here will be manually added to all events.
const CustomDimensions: any = {};
let Currency: string | undefined;

const GaReporter = {
    enabled: false,
    transactionId: generateUniqueId(),

    initTracking(measurementIds: string[], useCookies: boolean, cookiePath: string, applicationType: string) {
        GaReporter.enabled = true;
        if (!areCookiesEnabled()) {
            useCookies = false;
        }

        if (!useCookies) {
            // TODO: Decide if we should show consent banner. Setting analytics_storage to denied disables cookies,
            // but also makes no data show up at all in GA, so now we set temporary session cookies even without consent.
            // ReactGA.gtag("consent", "default", { ad_storage: "denied", analytics_storage: "denied" });
        }
        GaReporter.setCustomDimension("tracking_type", useCookies ? "cookie" : "none");
        GaReporter.setCustomDimension("application_type", applicationType);
        GaReporter.setCustomDimension("application_version", Constants.applicationVersion);

        const url = getCurrentCleanedUrl();
        const options: any[] = measurementIds.map(measurementId => ({
            trackingId: measurementId,
            gtagOptions: useCookies
                ? {
                      cookie_domain: "auto",
                      cookie_path: cookiePath,
                      cookie_flags: "secure;samesite=none",
                      send_page_view: false,
                      page_location: url.href,
                  }
                : {
                      // TODO: Disable cookies entirely while still collecting statistics.
                      cookie_domain: "auto",
                      cookie_path: cookiePath,
                      cookie_flags: "secure;samesite=none",
                      cookie_expires: 1, // Store a temporary cookie that is removed after 1 sec.
                      send_page_view: false,
                      page_location: url.href,
                  },
        }));

        ReactGA.initialize(options);
    },

    setCustomDimension(key: string, value: string) {
        CustomDimensions[key] = value;
    },

    updateTransactionId() {
        GaReporter.transactionId = generateUniqueId();
    },

    event(action: string, category?: string, label?: string, fields?: any) {
        if (!GaReporter.enabled) return;
        ReactGA.event(action, {
            event_category: category,
            event_label: label,
            ...fields,
            ...CustomDimensions,
        });
    },

    trackPageView() {
        if (!GaReporter.enabled) return;
        const url = getCurrentCleanedUrl();
        const path = url.pathname.toLowerCase();
        ReactGA.gtag("set", "page_location", url.href);
        GaReporter.event("page_view", undefined, undefined, { page_path: path });
    },

    trackProductClick(item: Item, price: DisplayPrice | null, itemList: string) {
        if (!GaReporter.enabled) return;
        const prod: GaProduct = {
            item_id: item.retailerId || undefined,
            item_name: item.displayText?.replace(/\n/g, " "),
            price: price?.priceValue,
        };

        const options: any = {
            item_list_name: itemList,
            items: [prod],
        };
        GaReporter.event("view_item", "E-commerce", undefined, options);
    },

    trackItemActivation(item: Item, price: DisplayPrice | null) {
        if (!GaReporter.enabled) return;
        const prod: GaProduct = {
            item_id: item.retailerId || undefined,
            item_name: item.displayText?.replace(/\n/g, " "),
            price: price?.priceValue,
        };

        const options: any = {
            item_list_name: "Summary items",
            items: [prod],
        };
        GaReporter.event("add_to_cart", "E-commerce", undefined, options);
    },

    trackProductCheckout(summaryItems: SummaryItem[], itemStates: ItemStates, trackPrice: boolean, totalPrice: number) {
        if (!GaReporter.enabled) return;
        const items: GaProduct[] = summaryItems.map(s => ({
            item_id: s.item.retailerId || undefined,
            item_name: s.item.displayText || undefined,
            price: trackPrice ? itemStates.getPrice(s.item.id)?.priceValue : undefined,
            quantity: s.amount,
        }));

        const options: any = {
            transaction_id: GaReporter.transactionId,
            currency: Currency,
            price: trackPrice ? totalPrice : undefined,
            items: items,
        };

        GaReporter.event("purchase", "E-commerce", undefined, options);
    },

    setProject(project: string) {
        GaReporter.setCustomDimension("application_name", project);
    },

    setModelId(modelId: string) {
        GaReporter.setCustomDimension("model", modelId);
    },

    setDealerId(dealerId: string) {
        GaReporter.setCustomDimension("dealer_id", dealerId);
    },

    // Sets the currency of prices.
    // This only supports three letter currency codes: https://support.google.com/analytics/answer/6205902?hl=sv&ref_topic=1037061#supported-currencies
    setCurrency(currency: string) {
        const supportedCurrencies = [
            "USD",
            "AED",
            "ARS",
            "AUD",
            "BGN",
            "BOB",
            "BRL",
            "CAD",
            "CHF",
            "CLP",
            "CNY",
            "COP",
            "CZK",
            "DKK",
            "EGP",
            "EUR",
            "FRF",
            "GBP",
            "HKD",
            "HRK",
            "HUF",
            "IDR",
            "ILS",
            "INR",
            "JPY",
            "KRW",
            "LTL",
            "MAD",
            "MXN",
            "MYR",
            "NOK",
            "NZD",
            "PEN",
            "PHP",
            "PKR",
            "PLN",
            "RON",
            "RSD",
            "RUB",
            "SAR",
            "SEK",
            "SGD",
            "THB",
            "TRY",
            "TWD",
            "UAH",
            "VEF",
            "VND",
            "ZAR",
        ];

        if (supportedCurrencies.indexOf(currency)) {
            Currency = currency;
        } else {
            // eslint-disable-next-line no-console
            console.warn("Unsupported three letter currency code for analytics: ", currency);
        }
    },
};

export default GaReporter;
